import styled from 'styled-components';

import { Member } from '@/entities/vfo/types';
import { UserCell, WidgetContainer } from '@/features/vfo/components';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { BFC } from '@/shared/types';
import { Icon } from '@/shared/uikit/Icon';
import { Button } from '@/shared/uikitV2/Button';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals';

type MembersGridProps = {
  members: Member[];
  maxItems?: number;
  caseId?: string;
};

export const MembersGrid: BFC<MembersGridProps> = ({ members, caseId, maxItems = 9, ...props }) => {
  const { t } = useTranslation('features/vfo', {
    keyPrefix: 'membersList',
  });
  const { t: tc } = useTranslation('features/vfo', {
    keyPrefix: `membersList.${caseId ? 'caseMembers' : 'allMembers'}`,
  });
  const modals = useModalsStore();
  const router = useAppRouter();

  const displayedMembers = members.slice(0, maxItems);

  const handleViewAllClick = () => {
    router.pushShallow(caseId ? `/vfo/case/${caseId}/members` : '/vfo/members');
  };

  if (members.length === 0) {
    return (
      <WidgetContainerStyled {...props}>
        <Header>
          <TitleWrapper>
            <Title>{tc('title')}</Title>
            <span>(0)</span>
          </TitleWrapper>
        </Header>
        <Column>
          <Column style={{ gap: 12 }}>
            <Icon style={{ color: COLORS.systemDark100 }} name="users-three" size="s44" />
            <EmptyText>{tc('noMembers')}</EmptyText>
          </Column>
          <Button
            type="default"
            onClick={() => {
              if (caseId) {
                modals.openModal({
                  type: 'AddMembersToCaseModal',
                  caseId,
                });
              } else {
                modals.openModal({
                  type: 'InviteMemberModal',
                });
              }
            }}
          >
            {tc('addNewBtn')}
          </Button>
        </Column>
      </WidgetContainerStyled>
    );
  }

  return (
    <WidgetContainerStyled {...props}>
      <Header>
        <TitleWrapper>
          <Title>{tc('title')}</Title>
          <span>({members.length})</span>
        </TitleWrapper>
        <LinkStyled type="text" size="middle" onClick={handleViewAllClick}>
          {t('viewAll')}
        </LinkStyled>
      </Header>
      <GridContainer className="uikitScrollbar">
        {displayedMembers.map((member) => (
          <GridItem key={member.profileId}>
            <UserCell small member={member} showStatus />
          </GridItem>
        ))}
      </GridContainer>
    </WidgetContainerStyled>
  );
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
`;

const Title = styled.span`
  font-weight: 600;
`;

const LinkStyled = styled(Button)`
  &&& {
    color: ${COLORS.systemSky200};
    font-weight: 700;
    height: 22px;

    &:hover {
      color: ${COLORS.systemSky200};
    }
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  row-gap: 32px;
  overflow: auto;
  max-height: 100%;

  ${media.sm} {
    grid-template-columns: 1fr;
    padding-bottom: 32px;
  }
`;

const GridItem = styled.div`
  display: flex;
  align-items: center;
`;

const EmptyText = styled.span`
  color: ${COLORS.systemDark200};
  font-weight: 600;
`;

const WidgetContainerStyled = styled(WidgetContainer)`
  max-height: 270px;
  overflow: hidden;

  container-type: inline-size;
  container-name: main;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
`;
